import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useToast } from '../../context/ToastContext';
import { useLoading } from '../../context/LoadingContext';
import './OtherCourtCaseForm.scss';
import { AddOtherCourtCases, getBothCourtCasesDetailsByCourtRef, UpdateOtherManualCase } from '../../services/api/CasesApi';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { uploadFileWithProgress } from '../../utils/ftpUploadUtils';

export const OtherCourtCaseForm = ({ isEdit = false }) => {
    const { showToast } = useToast();

    const { company } = useAuth();
    const [uploadingOrders, setUploadingOrders] = useState({});
    const [uploadProgressOrders, setUploadProgressOrders] = useState({});
    const formRef = useRef(null);
    const caseHistoryRefs = useRef([]);
    const actsDataRefs = useRef([]);
    const iaDetailsRefs = useRef([]);
    const orderInfoRefs = useRef([]);
    const transferDocsRefs = useRef([]);
    const objectionDetailsRefs = useRef([]);
    const documentsRefs = useRef([]);
    const historyCaseDataRefs = useRef([]);
    const submitSectionRef = useRef(null);
    const { isLoading, showLoading, hideLoading } = useLoading();
    const [activeCaseHistoryIndex, setActiveCaseHistoryIndex] = useState(0);
    const [activeActsDataIndex, setActiveActsDataIndex] = useState(0);
    const [activeIaDetailsIndex, setActiveIaDetailsIndex] = useState(0);
    const [activeOrderInfoIndex, setActiveOrderInfoIndex] = useState(0);
    const [activeTransferDocsIndex, setActiveTransferDocsIndex] = useState(0);
    const [activeObjectionDetailsIndex, setActiveObjectionDetailsIndex] = useState(0);
    const [activeDocumentsIndex, setActiveDocumentsIndex] = useState(0);
    const [activeHistoryCaseDataIndex, setActiveHistoryCaseDataIndex] = useState(0);

    const [formData, setFormData] = useState({
        courtName: '',
        serialNumber: '',
        caseTypeAndNumber: '',
        parties: '',
        advocates: '',
        viewDetails: {
            type: '',
            caseId: '',
            courtRef: '',
            index: ''
        },
        caseDetails: {
            "Filing Number": "",
            "Filing Date": "",
            "Registration Number": "",
            "Registration Date": "",
            "CNR Number": ""
        },
        caseStatus: {
            "Case Status": "",
            "First Hearing Date": "",
            "Nature of Disposal": "",
            Coram: "",
            benchType: "",
            "Judicial Branch": "",
            State: "",
            District: "",
            "Decision Date": "",
            "Next Hearing Date": "",
        },
        caseHistory: [{
            causeListType: "",
            judge: "",
            businessOnDate: "",
            hearingDate: "",
            purposeOfHearing: ""
        }],
        actsData: [{ underAct: "", underSection: "" }],
        subordinateCourtInfo: {
            "Court Number and Name": "",
            "Case Number and Year": "",
            "Case Decision Date": "",
            "State": "",
            "District": ""
        },
        petitionerAndAdvocate: {
            petitioner: "",
            advocate: "",
            respondent: "",
            respondentAdvocate: ""
        },
        iaDetails: [{ iaNumber: "", party: "", filingDate: "", nextDate: "", iaStatus: "" }],
        orderInfo: [{
            orderNumber: "",
            orderOn: "",
            judge: "",
            orderDate: "",
            orderPdfLink: ""
        }],
        transferDocs: [{ srNo: "", documentNo: "", dateOfReceiving: "", filedBy: "", advocateName: "", documentFiled: "" }],
        objectionDetails: [{ srNo: "", scrutinyDate: "", objection: "", complianceDate: "", receiptDate: "" }],
        documents: [{ srNo: "", documentNo: "", dateOfReceiving: "", filedBy: "", advocateName: "", documentFiled: "" }],
        historyCaseData: [{ causeListType: "", judge: "", businessOnDate: "", hearingDate: "", purposeOfHearing: "" }],
    });
    const navigate = useNavigate()
    const { courtRef } = useParams();
    const formInputRef = useRef(null);
    useEffect(() => {
        // Auto-focus the fetch input when creating a new case (not editing)
        if (formInputRef && formInputRef.current) {
            // timeout ensures the element is mounted
            setTimeout(() => {
                try {
                    formInputRef.current.focus();
                    const val = formInputRef.current.value || '';
                    formInputRef.current.setSelectionRange(val.length, val.length);
                } catch (e) {
                    // ignore
                }
            }, 0);
        }
    }, [isEdit, courtRef]);


    const handleOrderFileUpload = async (selectedFile, index) => {
        if (!selectedFile) return;

        const orderKey = `order_${index}`;
        setUploadingOrders(prev => ({ ...prev, [orderKey]: true }));
        setUploadProgressOrders(prev => ({ ...prev, [orderKey]: 0 }));

        try {
            const filePath = company?._id ? `/${company?._id}/oc/added/${formData.viewDetails.courtRef}/orders` : '/unknown/orders';

            await uploadFileWithProgress(
                selectedFile,
                filePath,
                // Progress callback
                (progress) => {
                    setUploadProgressOrders(prev => ({ ...prev, [orderKey]: progress }));
                },
                // Success callback
                (result) => {
                    const updatedOrders = [...formData.orderInfo];
                    updatedOrders[index].orderPdfLink = result.imageUrl;
                    setFormData(prev => ({ ...prev, orderInfo: updatedOrders }));
                    setUploadingOrders(prev => ({ ...prev, [orderKey]: false }));
                    showToast('Order file uploaded successfully!', 'success');
                },
                // Error callback
                (error) => {
                    console.error("Order file upload failed:", error);
                    setUploadingOrders(prev => ({ ...prev, [orderKey]: false }));
                    setUploadProgressOrders(prev => ({ ...prev, [orderKey]: 0 }));
                    showToast(error.message || 'Order file upload failed', 'error');
                }
            );
        } catch (error) {
            console.error("Order file upload failed:", error);
            setUploadingOrders(prev => ({ ...prev, [orderKey]: false }));
            setUploadProgressOrders(prev => ({ ...prev, [orderKey]: 0 }));
            showToast(error.message || 'Order file upload failed', 'error');
        }
    };

    const handleOrderFileChange = (e, index) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            handleOrderFileUpload(selectedFile, index);
        }
    };

    const handleRemoveOrderFile = (index) => {
        const updatedOrders = [...formData.orderInfo];
        updatedOrders[index].orderPdfLink = "";
        setFormData(prev => ({ ...prev, orderInfo: updatedOrders }));

        // Clear upload states
        const orderKey = `order_${index}`;
        setUploadingOrders(prev => ({ ...prev, [orderKey]: false }));
        setUploadProgressOrders(prev => ({ ...prev, [orderKey]: 0 }));
    };

    const convertDateFormat = (dateStr) => {
        if (!dateStr) return '';

        // Convert from DD/MM/YYYY to YYYY-MM-DD for input fields
        const ddmmyyyyMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (ddmmyyyyMatch) {
            const [, day, month, year] = ddmmyyyyMatch;
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }

        return dateStr;
    };
    // Helper 1: Convert YYYY-MM-DD to "14th August 2014" (For caseStatus)
    const formatToReadableDate = (dateStr) => {
        if (!dateStr || typeof dateStr !== 'string') return "";
        const parts = dateStr.split('-');
        if (parts.length !== 3) return dateStr;

        const [year, month, day] = parts;
        const dayNum = parseInt(day, 10);
        const monthIndex = parseInt(month, 10) - 1;

        const monthNames = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];

        const getOrdinal = (n) => {
            if (n > 3 && n < 21) return n + 'th';
            switch (n % 10) {
                case 1: return n + "st";
                case 2: return n + "nd";
                case 3: return n + "rd";
                default: return n + "th";
            }
        };

        return `${getOrdinal(dayNum)} ${monthNames[monthIndex]} ${year}`;
    };

    // Helper 2: Convert YYYY-MM-DD to "DD-MM-YYYY" (For caseDetails)
    const formatToDDMMYYYY = (dateStr) => {
        if (!dateStr || typeof dateStr !== 'string') return "";
        const parts = dateStr.split('-'); // Expecting YYYY-MM-DD
        if (parts.length !== 3) return dateStr;
        const [year, month, day] = parts;
        return `${day}-${month}-${year}`;
    };

    // Function to populate form with API data
    const populateFormWithAPIData = useCallback((apiData) => {
        const convertDates = (obj) => {
            const converted = { ...obj };

            // Convert specific date fields
            const dateFields = [
                'Filing Date', 'Registration Date', 'Case Decision Date',
                'Decision Date', 'filingDate', 'nextDate', 'orderDate',
                'businessOnDate', 'hearingDate', 'dateOfReceiving',
                'scrutinyDate', 'complianceDate', 'receiptDate'
            ];

            Object.keys(converted).forEach(key => {
                if (dateFields.includes(key) && converted[key]) {
                    converted[key] = convertDateFormat(converted[key]);
                } else if (typeof converted[key] === 'object' && converted[key] !== null) {
                    if (Array.isArray(converted[key])) {
                        converted[key] = converted[key].map(item =>
                            typeof item === 'object' ? convertDates(item) : item
                        );
                    } else {
                        converted[key] = convertDates(converted[key]);
                    }
                }
            });

            return converted;
        };

        const convertedData = convertDates(apiData);
        setFormData(convertedData);
    }, []);




    // Fetch case details if in edit mode
    useEffect(() => {
        if (isEdit && courtRef) {
            showLoading('Loading case details...');
            getBothCourtCasesDetailsByCourtRef(courtRef)
                .then((response) => {
                    if (response.data && response.data.case) {
                        const caseData = response.data.case;
                        // Use the populateFormWithAPIData function to handle date conversions and field structures
                        populateFormWithAPIData(caseData);
                        showToast('Case details loaded successfully!', 'success');
                    }
                })
                .catch((error) => {
                    console.error('Error fetching case details:', error);
                    showToast('Failed to load case details. Please try again.', 'error');
                })
                .finally(() => {
                    hideLoading();
                });
        }
    }, [isEdit, courtRef]);

    useEffect(() => {
        const submitSection = submitSectionRef.current;
        const submitSectionTop = document.querySelector('.oc-submit-section');

        if (!submitSection || !submitSectionTop) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    submitSectionTop.classList.add('hidden');
                } else {
                    submitSectionTop.classList.remove('hidden');
                }
            },
            {
                root: null, // Use viewport as root
                threshold: 0.1 // Trigger when 10% of .hc-submit-section is visible
            }
        );

        observer.observe(submitSection);

        return () => {
            observer.unobserve(submitSection);
        };
    }, []);

    const handleTopSubmit = () => {
        if (formRef.current) {
            formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
    };
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleNestedChange = (e, section) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [section]: { ...formData[section], [name]: value }
        });
    };

    const handleArrayChange = (index, section, field, value) => {
        const updatedArray = [...formData[section]];
        updatedArray[index][field] = value;
        setFormData({ ...formData, [section]: updatedArray });
    };

    const addArrayItem = (section) => {
        const newItem = {};

        if (section === 'orderInfo') {
            newItem.orderNumber = "";
            newItem.orderOn = "";
            newItem.judge = "";
            newItem.orderDate = "";
            newItem.orderPdfLink = "";
        }
        else if (section === 'caseHistory') {
            newItem.causeListType = "";
            newItem.judge = "";
            newItem.businessOnDate = "";
            newItem.hearingDate = "";
            newItem.purposeOfHearing = "";
        } else if (section === 'actsData') {
            newItem.act = "";
            newItem.section = "";
        } else if (section === 'iaDetails') {
            newItem.iaNumber = "";
            newItem.party = "";
            newItem.filingDate = "";
            newItem.nextDate = "";
            newItem.iaStatus = "";
        } else if (section === 'transferDocs') {
            newItem.srNo = "";
            newItem.documentNo = "";
            newItem.dateOfReceiving = "";
            newItem.filedBy = "";
            newItem.advocateName = "";
            newItem.documentFiled = "";
        } else if (section === 'objectionDetails') {
            newItem.srNo = "";
            newItem.scrutinyDate = "";
            newItem.objection = "";
            newItem.complianceDate = "";
            newItem.receiptDate = "";
        } else if (section === 'documents') {
            newItem.srNo = "";
            newItem.documentNo = "";
            newItem.dateOfReceiving = "";
            newItem.filedBy = "";
            newItem.advocateName = "";
            newItem.documentFiled = "";
        } else if (section === 'historyCaseData') {
            newItem.causeListType = "";
            newItem.judge = "";
            newItem.businessOnDate = "";
            newItem.hearingDate = "";
            newItem.purposeOfHearing = "";
        }
        setFormData(prev => {
            const updated = { ...prev, [section]: [...prev[section], newItem] };
            const newArray = [...prev[section], newItem];

            if (section === "orderInfo") {
                setActiveOrderInfoIndex(newArray.length - 1);
            } else if (section === "actsData") {
                setActiveActsDataIndex(newArray.length - 1);
            } else if (section === "caseHistory") {
                setActiveCaseHistoryIndex(newArray.length - 1);
            } else if (section === "iaDetails") {
                setActiveIaDetailsIndex(newArray.length - 1);
            } else if (section === "transferDocs") {
                setActiveTransferDocsIndex(newArray.length - 1);
            } else if (section === "objectionDetails") {
                setActiveObjectionDetailsIndex(newArray.length - 1);
            } else if (section === "documents") {
                setActiveDocumentsIndex(newArray.length - 1);
            } else if (section === "historyCaseData") {
                setActiveHistoryCaseDataIndex(newArray.length - 1);
            }
            // Focus after state update
            setTimeout(() => {
                if (section === 'caseHistory') {
                    const idx = updated.caseHistory.length - 1;
                    if (caseHistoryRefs.current[idx]) caseHistoryRefs.current[idx].focus();
                } else if (section === 'actsData') {
                    const idx = updated.actsData.length - 1;
                    if (actsDataRefs.current[idx]) actsDataRefs.current[idx].focus();
                } else if (section === 'iaDetails') {
                    const idx = updated.iaDetails.length - 1;
                    if (iaDetailsRefs.current[idx]) iaDetailsRefs.current[idx].focus();
                } else if (section === 'orderInfo') {
                    const idx = updated.orderInfo.length - 1;
                    if (orderInfoRefs.current[idx]) orderInfoRefs.current[idx].focus();
                } else if (section === 'transferDocs') {
                    const idx = updated.transferDocs.length - 1;
                    if (transferDocsRefs.current[idx]) transferDocsRefs.current[idx].focus();
                } else if (section === 'objectionDetails') {
                    const idx = updated.objectionDetails.length - 1;
                    if (objectionDetailsRefs.current[idx]) objectionDetailsRefs.current[idx].focus();
                } else if (section === 'documents') {
                    const idx = updated.documents.length - 1;
                    if (documentsRefs.current[idx]) documentsRefs.current[idx].focus();
                } else if (section === 'historyCaseData') {
                    const idx = updated.historyCaseData.length - 1;
                    if (historyCaseDataRefs.current[idx]) historyCaseDataRefs.current[idx].focus();
                }
            }, 0);
            return updated;
        });
    };

    const removeArrayItem = (index, section) => {
        const updatedArray = formData[section].filter((_, i) => i !== index);
        setFormData({ ...formData, [section]: updatedArray });
        if (section === "orderInfo") {
            if (index === activeOrderInfoIndex) setActiveOrderInfoIndex(updatedArray.length > 0 ? updatedArray.length - 1 : 0);
            else if (index < activeOrderInfoIndex) setActiveOrderInfoIndex(prev => prev - 1);
        } else if (section === "actsData") {
            if (index === activeActsDataIndex) setActiveActsDataIndex(updatedArray.length > 0 ? updatedArray.length - 1 : 0);
            else if (index < activeActsDataIndex) setActiveActsDataIndex(prev => prev - 1);
        } else if (section === "caseHistory") {
            if (index === activeCaseHistoryIndex) setActiveCaseHistoryIndex(updatedArray.length > 0 ? updatedArray.length - 1 : 0);
            else if (index < activeCaseHistoryIndex) setActiveCaseHistoryIndex(prev => prev - 1);
        } else if (section === "iaDetails") {
            if (index === activeIaDetailsIndex) setActiveIaDetailsIndex(updatedArray.length > 0 ? updatedArray.length - 1 : 0);
            else if (index < activeIaDetailsIndex) setActiveIaDetailsIndex(prev => prev - 1);
        } else if (section === "transferDocs") {
            if (index === activeTransferDocsIndex) setActiveTransferDocsIndex(updatedArray.length > 0 ? updatedArray.length - 1 : 0);
            else if (index < activeTransferDocsIndex) setActiveTransferDocsIndex(prev => prev - 1);
        } else if (section === "objectionDetails") {
            if (index === activeObjectionDetailsIndex) setActiveObjectionDetailsIndex(updatedArray.length > 0 ? updatedArray.length - 1 : 0);
            else if (index < activeObjectionDetailsIndex) setActiveObjectionDetailsIndex(prev => prev - 1);
        } else if (section === "documents") {
            if (index === activeDocumentsIndex) setActiveDocumentsIndex(updatedArray.length > 0 ? updatedArray.length - 1 : 0);
            else if (index < activeDocumentsIndex) setActiveDocumentsIndex(prev => prev - 1);
        } else if (section === "historyCaseData") {
            if (index === activeHistoryCaseDataIndex) setActiveHistoryCaseDataIndex(updatedArray.length > 0 ? updatedArray.length - 1 : 0);
            else if (index < activeHistoryCaseDataIndex) setActiveHistoryCaseDataIndex(prev => prev - 1);
        }

    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const partiesEmpty = !formData.parties || !formData.parties.trim();
        const courtRefEmpty = !formData.viewDetails.courtRef || !formData.viewDetails.courtRef.trim();
        const courtNameEmpty = !formData.courtName || !formData.courtName.trim();

        if (courtNameEmpty && partiesEmpty && courtRefEmpty) {
            showToast('Court Name, Parties and Court Reference are required.', 'error');
            return;
        }

        if ((partiesEmpty && courtRefEmpty) || (partiesEmpty && courtNameEmpty) || (courtNameEmpty && courtRefEmpty)) {
            const missing = [];
            if (courtNameEmpty) missing.push('Court Name');
            if (partiesEmpty) missing.push('Parties');
            if (courtRefEmpty) missing.push('Court Reference');
            showToast(`${missing.join(' and ')} ${missing.length > 1 ? 'are' : 'is'} required.`, 'error');
            return;
        }

        if (courtNameEmpty) {
            showToast('Court Name field is required.', 'error');
            return;
        }
        if (partiesEmpty) {
            showToast('Parties field is required.', 'error');
            return;
        }
        if (courtRefEmpty) {
            showToast('Court Reference field is required.', 'error');
            return;
        }
        showLoading('Processing...');

        try {

            const processedCaseStatus = { ...formData.caseStatus };
            const statusDateFields = ["First Hearing Date", "Decision Date", "Next Hearing Date"];

            statusDateFields.forEach(field => {
                if (processedCaseStatus[field]) {
                    processedCaseStatus[field] = formatToReadableDate(processedCaseStatus[field]);
                }
            });

            // Filter empty keys in Case Status
            const filteredCaseStatus = {};
            Object.keys(processedCaseStatus).forEach(key => {
                if (processedCaseStatus[key] && processedCaseStatus[key].trim() !== '') {
                    filteredCaseStatus[key] = processedCaseStatus[key];
                }
            });

            // --- B. Format Case Details Dates (Target: "DD-MM-YYYY") ---
            const processedCaseDetails = { ...formData.caseDetails };
            const detailsDateFields = ["Filing Date", "Registration Date"];

            detailsDateFields.forEach(field => {
                if (processedCaseDetails[field]) {
                    processedCaseDetails[field] = formatToDDMMYYYY(processedCaseDetails[field]);
                }
            });

            // --- C. Construct the Payload ---
            const payload = {
                ...formData,
                caseStatus: filteredCaseStatus,     // Uses "14th August 2014" format
                caseDetails: processedCaseDetails,  // Uses "DD-MM-YYYY" format
                serialNumber: Number(formData.serialNumber),
            };

            let response;

            if (isEdit && courtRef) {
                response = await UpdateOtherManualCase(courtRef, payload);
                showToast('Case updated successfully!', 'success');
            } else {
                response = await AddOtherCourtCases(payload);
                showToast('Case submitted successfully!', 'success');
            }

            setTimeout(() => {
                navigate('/home/cases');
            }, 1500);
        } catch (error) {
            console.error("Submission error:", error?.response?.data || error.message);
            showToast('Failed to submit. Please check all fields and try again.', 'error');
        } finally {
            hideLoading();
        }
    };
    return (
        <>
            <div className="high-court-case-form-container">
                <div className="hc-form-header">
                    <h1 className="hc-form-title">{isEdit ? 'Edit Other Court Case' : 'Other Court Case Form'}</h1>
                    <p className="hc-form-subtitle">Complete case information management system</p>
                </div>
                <form onSubmit={handleSubmit} ref={formRef}>
                    <h2 className='hc-title'>Basic Details</h2>
                    <div className='div-rem'>
                        <div className="other-court-form-group">
                            <div className='form-det'>
                                <label>Court Name  <span className="user-form-required">*</span></label>
                                <input ref={formInputRef} name="courtName" value={formData.courtName} onChange={handleChange} />
                            </div>
                            <div className='form-det'>
                                <label>Serial Number</label>
                                <input name="serialNumber" value={formData.serialNumber} onChange={handleChange} />
                            </div>
                            <div className='form-det'>
                                <label>Case Type & Number</label>
                                <input name="caseTypeAndNumber" value={formData.caseTypeAndNumber} onChange={handleChange} />
                            </div>
                        </div>
                        <div className="other-court-form-group">
                            <div className='form-det'>
                                <label>Parties  <span className="user-form-required">*</span></label>
                                <input name="parties" value={formData.parties} onChange={handleChange} />
                            </div>
                            <div className='form-det'>
                                <label>Advocates</label>
                                <input name="advocates" value={formData.advocates} onChange={handleChange} />
                            </div>
                        </div>
                    </div>

                    <h2 className='hc-title'>View Details</h2>
                    <div className='div-rem'>
                        <div className="other-court-form-group">
                            <div className='form-det'>
                                <label>Type</label>
                                <input
                                    name="type"
                                    value={formData.viewDetails.type}
                                    onChange={(e) => handleNestedChange(e, 'viewDetails')}
                                />
                            </div>
                            <div className='form-det'>
                                <label>Case ID</label>
                                <input
                                    name="caseId"
                                    value={formData.viewDetails.caseId}
                                    onChange={(e) => handleNestedChange(e, 'viewDetails')}
                                />
                            </div>
                        </div>
                        <div className="other-court-form-group">
                            <div className='form-det'>
                                <label>Court Reference  <span className="user-form-required">*</span></label>
                                <input
                                    name="courtRef"
                                    value={formData.viewDetails.courtRef}
                                    onChange={(e) => handleNestedChange(e, 'viewDetails')}
                                />
                            </div>
                            <div className='form-det'>
                                <label>Index</label>
                                <input
                                    name="index"
                                    value={formData.viewDetails.index}
                                    onChange={(e) => handleNestedChange(e, 'viewDetails')}
                                />
                            </div>
                        </div>
                    </div>

                    <h2 className='hc-title'>Case Details</h2>
                    <div className='div-rem'>
                        <div className="other-court-form-group">
                            <div className='form-det'>
                                <label>Filing Number</label>
                                <input
                                    name="Filing Number"
                                    value={formData.caseDetails["Filing Number"]}
                                    onChange={(e) => handleNestedChange(e, 'caseDetails')}
                                />
                            </div>
                            <div className='form-det'>
                                <label>Filing Date</label>
                                <input
                                    type="date"
                                    name="Filing Date"
                                    value={formData.caseDetails["Filing Date"]}
                                    onChange={(e) => handleNestedChange(e, 'caseDetails')}
                                />
                            </div>
                        </div>
                        <div className="other-court-form-group">
                            <div className='form-det'>
                                <label>Registration Number</label>
                                <input
                                    name="Registration Number"
                                    value={formData.caseDetails["Registration Number"]}
                                    onChange={(e) => handleNestedChange(e, 'caseDetails')}
                                />
                            </div>
                            <div className='form-det'>
                                <label>Registration Date</label>
                                <input
                                    type="date"
                                    name="Registration Date"
                                    value={formData.caseDetails["Registration Date"]}
                                    onChange={(e) => handleNestedChange(e, 'caseDetails')}
                                />
                            </div>
                            <div className='form-det'>
                                <label>CNR Number</label>
                                <input
                                    name="CNR Number"
                                    value={formData.caseDetails["CNR Number"]}
                                    onChange={(e) => handleNestedChange(e, 'caseDetails')}
                                />
                            </div>
                        </div>
                    </div>



                    <h2 className="hc-title">Case Status</h2>
                    <div className="div-rem">
                        <div className="high-court-form-group">
                            <div className="form-det">
                                <label>Case Status</label>
                                <select
                                    name="Case Status"
                                    value={formData.caseStatus["Case Status"]}
                                    onChange={(e) => handleNestedChange(e, "caseStatus")}
                                >
                                    <option value="">Select Status</option>
                                    <option value="pending">Pending</option>
                                    <option value="disposed">Disposed</option>
                                    <option value="CASE DISPOSED">CASE DISPOSED</option>
                                </select>
                            </div>
                            <div className="form-det">
                                <label>First Hearing Date</label>
                                <input
                                    type="date"
                                    name="First Hearing Date"
                                    value={formData.caseStatus["First Hearing Date"]}
                                    onChange={(e) => handleNestedChange(e, "caseStatus")}
                                />
                            </div>
                        </div>
                        <div className="high-court-form-group">
                            <div className="form-det">
                                <label>Decision Date</label>
                                <input
                                    type="date"
                                    name="Decision Date"
                                    value={formData.caseStatus["Decision Date"]}
                                    onChange={(e) => handleNestedChange(e, "caseStatus")}
                                />
                            </div>
                            <div className="form-det">
                                <label>Nature of Disposal</label>
                                <input
                                    name="Nature of Disposal"
                                    value={formData.caseStatus["Nature of Disposal"]}
                                    onChange={(e) => handleNestedChange(e, "caseStatus")}
                                />
                            </div>
                        </div>
                        <div className="high-court-form-group">
                            <div className="form-det">
                                <label>Bench Type</label>
                                <input
                                    name="benchType"
                                    value={formData.caseStatus.benchType}
                                    onChange={(e) => handleNestedChange(e, "caseStatus")}
                                />
                            </div>
                            <div className="form-det">
                                <label>Judicial Branch</label>
                                <input
                                    name="Judicial Branch"
                                    value={formData.caseStatus["Judicial Branch"]}
                                    onChange={(e) => handleNestedChange(e, "caseStatus")}
                                />
                            </div>
                        </div>
                        <div className="high-court-form-group">
                            <div className="form-det">
                                <label>State</label>
                                <input
                                    name="State"
                                    value={formData.caseStatus.State}
                                    onChange={(e) => handleNestedChange(e, "caseStatus")}
                                />
                            </div>
                            <div className="form-det">
                                <label>District</label>
                                <input
                                    name="District"
                                    value={formData.caseStatus.District}
                                    onChange={(e) => handleNestedChange(e, "caseStatus")}
                                />
                            </div>
                        </div>
                        <div className="high-court-form-group">


                            <div className="form-det">
                                <label>Coram</label>
                                <input
                                    name="coram"
                                    value={formData.caseStatus.coram}
                                    onChange={(e) => handleNestedChange(e, "caseStatus")}
                                />
                            </div>
                            <div className='form-det'>
                                <label>Next Hearing Date</label>
                                <input
                                    type="date"
                                    name="Next Hearing Date"
                                    value={formData.caseStatus["Next Hearing Date"]}
                                    onChange={(e) => handleNestedChange(e, "caseStatus")}
                                />
                            </div>

                        </div>
                    </div>

                    {/* <h2 className='hc-title'>Case History</h2>
                    {formData.caseHistory.map((item, index) => (
                        <div className='div-rem' key={index}>
                            <div>
                                <div className="other-court-form-group">
                                    <div className='form-det'>
                                        <label>Cause List Type</label>
                                        <input
                                            name="causeListType"
                                            value={item.causeListType}
                                            ref={el => caseHistoryRefs.current[index] = el}
                                            onChange={(e) => handleArrayChange(index, 'caseHistory', 'causeListType', e.target.value)}
                                        />
                                    </div>
                                    <div className='form-det'>
                                        <label>Judge</label>
                                        <input
                                            name="judge"
                                            value={item.judge}
                                            onChange={(e) => handleArrayChange(index, 'caseHistory', 'judge', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="other-court-form-group">
                                    <div className='form-det'>
                                        <label>Business On Date</label>
                                        <input
                                            type="date"
                                            name="businessOnDate"
                                            value={item.businessOnDate}
                                            onChange={(e) => handleArrayChange(index, 'caseHistory', 'businessOnDate', e.target.value)}
                                        />
                                    </div>
                                    <div className='form-det'>
                                        <label>Hearing Date</label>
                                        <input
                                            type="date"
                                            name="hearingDate"
                                            value={item.hearingDate}
                                            onChange={(e) => handleArrayChange(index, 'caseHistory', 'hearingDate', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="other-court-form-group">
                                    <div className='form-det'>
                                        <label>Purpose of Hearing</label>
                                        <input
                                            name="purposeOfHearing"
                                            value={item.purposeOfHearing}
                                            onChange={(e) => handleArrayChange(index, 'caseHistory', 'purposeOfHearing', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className='rem-div-btn'>
                                <button type="button" className='court-remove-case' onClick={() => removeArrayItem(index, 'caseHistory')}>Remove</button>
                            </div>
                        </div>
                    ))}
                    <div className='add-div-btn'>
                        <button type="button" onClick={() => addArrayItem('caseHistory')} className='court-add-case'>+ Case History</button>
                    </div> */}

                    <h2 className='hc-title'>Case History</h2>
                    {formData.caseHistory.map((item, index) => {
                        const isExpanded = index === activeCaseHistoryIndex;
                        const hasData = item.purposeOfHearing || item.hearingDate || item.judge;

                        return (
                            <div className={`div-rem ${!isExpanded && hasData ? 'collapsed' : ''}`} key={index}>
                                {!isExpanded && hasData ? (
                                    <div className="summary-view">
                                        <div className="summary-details">
                                            <div className="summary-item">
                                                <span className="summary-label">Purpose:</span>
                                                <span className="summary-value">{item.purposeOfHearing || 'N/A'}</span>
                                            </div>
                                            <div className="summary-item">
                                                <span className="summary-label">Date:</span>
                                                <span className="summary-value">{item.hearingDate || 'N/A'}</span>
                                            </div>
                                        </div>
                                        <div className="summary-actions">
                                            <button type="button" className="edit-btn" onClick={() => setActiveCaseHistoryIndex(index)}>Edit</button>
                                            <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'caseHistory')}>Remove</button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div>
                                            <div className="other-court-form-group">
                                                <div className='form-det'>
                                                    <label>Cause List Type</label>
                                                    <input
                                                        name="causeListType"
                                                        value={item.causeListType}
                                                        ref={el => caseHistoryRefs.current[index] = el}
                                                        onChange={(e) => handleArrayChange(index, 'caseHistory', 'causeListType', e.target.value)}
                                                    />
                                                </div>
                                                <div className='form-det'>
                                                    <label>Judge</label>
                                                    <input
                                                        name="judge"
                                                        value={item.judge}
                                                        onChange={(e) => handleArrayChange(index, 'caseHistory', 'judge', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="other-court-form-group">
                                                <div className='form-det'>
                                                    <label>Business On Date</label>
                                                    <input
                                                        type="date"
                                                        name="businessOnDate"
                                                        value={item.businessOnDate}
                                                        onChange={(e) => handleArrayChange(index, 'caseHistory', 'businessOnDate', e.target.value)}
                                                    />
                                                </div>
                                                <div className='form-det'>
                                                    <label>Hearing Date</label>
                                                    <input
                                                        type="date"
                                                        name="hearingDate"
                                                        value={item.hearingDate}
                                                        onChange={(e) => handleArrayChange(index, 'caseHistory', 'hearingDate', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="other-court-form-group">
                                                <div className='form-det'>
                                                    <label>Purpose of Hearing</label>
                                                    <input
                                                        name="purposeOfHearing"
                                                        value={item.purposeOfHearing}
                                                        onChange={(e) => handleArrayChange(index, 'caseHistory', 'purposeOfHearing', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className='rem-div-btn'>
                                            <button type="button" className='court-remove-case' onClick={() => removeArrayItem(index, 'caseHistory')}>Remove</button>
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                    <div className='add-div-btn'>
                        <button type="button" onClick={() => addArrayItem('caseHistory')} className='court-add-case'>+ Case History</button>
                    </div>

                    {/* <h2 className='hc-title'>Acts Data</h2>
                    {formData.actsData.map((item, index) => (
                        <div className='div-rem' key={index}>
                            <div>
                                <div className="other-court-form-group">
                                    <div className='form-det'>
                                        <label>Act</label>
                                        <input
                                            name="act"
                                            value={item.act}
                                            ref={el => actsDataRefs.current[index] = el}
                                            onChange={(e) => handleArrayChange(index, 'actsData', 'act', e.target.value)}
                                        />
                                    </div>
                                    <div className='form-det'>
                                        <label>Section</label>
                                        <input
                                            name="section"
                                            value={item.section}
                                            onChange={(e) => handleArrayChange(index, 'actsData', 'section', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className='rem-div-btn'>
                                <button type="button" className='court-remove-case' onClick={() => removeArrayItem(index, 'actsData')}>Remove</button>
                            </div>
                        </div>
                    ))}
                    <div className='add-div-btn'>
                        <button type="button" className='court-add-case' onClick={() => addArrayItem('actsData')}>+ Acts Data</button>
                    </div> */}

                    <h2 className='hc-title'>Acts Data</h2>
                    {formData.actsData.map((item, index) => {
                        const isExpanded = index === activeActsDataIndex;
                        const hasData = item.underAct || item.underSection;

                        return (
                            <div className={`div-rem ${!isExpanded && hasData ? 'collapsed' : ''}`} key={index}>
                                {!isExpanded && hasData ? (
                                    <div className="summary-view">
                                        <div className="summary-details">
                                            <div className="summary-item">
                                                <span className="summary-label">Act:</span>
                                                <span className="summary-value">{item.underAct || 'N/A'}</span>
                                            </div>
                                            <div className="summary-item">
                                                <span className="summary-label">Section:</span>
                                                <span className="summary-value">{item.underSection || 'N/A'}</span>
                                            </div>
                                        </div>
                                        <div className="summary-actions">
                                            <button type="button" className="edit-btn" onClick={() => setActiveActsDataIndex(index)}>Edit</button>
                                            <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'actsData')}>Remove</button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div>
                                            <div className="other-court-form-group">
                                                <div className='form-det'>
                                                    <label>Act</label>
                                                    <input
                                                        name="underAct"
                                                        value={item.underAct}
                                                        ref={el => actsDataRefs.current[index] = el}
                                                        onChange={(e) => handleArrayChange(index, 'actsData', 'underAct', e.target.value)}
                                                    />
                                                </div>
                                                <div className='form-det'>
                                                    <label>Section</label>
                                                    <input
                                                        name="underSection"
                                                        value={item.underSection}
                                                        onChange={(e) => handleArrayChange(index, 'actsData', 'underSection', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className='rem-div-btn'>
                                            <button type="button" className='court-remove-case' onClick={() => removeArrayItem(index, 'actsData')}>Remove</button>
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                    <div className='add-div-btn'>
                        <button type="button" className='court-add-case' onClick={() => addArrayItem('actsData')}>+ Acts Data</button>
                    </div>

                    <h2 className='hc-title'>Subordinate Court Info</h2>
                    <div className='div-rem'>
                        <div className="other-court-form-group">
                            <div className='form-det'>
                                <label>Court Number and Name</label>
                                <input
                                    name="Court Number and Name"
                                    value={formData.subordinateCourtInfo["Court Number and Name"]}
                                    onChange={(e) => handleNestedChange(e, 'subordinateCourtInfo')}
                                />
                            </div>
                            <div className='form-det'>
                                <label>Case Number and Year</label>
                                <input
                                    type="text"
                                    name="Case Number and Year"
                                    value={formData.subordinateCourtInfo["Case Number and Year"]}
                                    onChange={(e) => handleNestedChange(e, 'subordinateCourtInfo')}
                                />
                            </div>
                        </div>
                        <div className="other-court-form-group">
                            <div className='form-det'>
                                <label>Case Decision Date</label>
                                <input
                                    type='date'
                                    name="Case Decision Date"
                                    value={formData.subordinateCourtInfo["Case Decision Date"]}
                                    onChange={(e) => handleNestedChange(e, 'subordinateCourtInfo')}
                                />
                            </div>
                            <div className='form-det'>
                                <label>State</label>
                                <input
                                    name="State"
                                    value={formData.subordinateCourtInfo["State"]}
                                    onChange={(e) => handleNestedChange(e, 'subordinateCourtInfo')}
                                />
                            </div>
                            <div className='form-det'>
                                <label>District</label>
                                <input
                                    name="District"
                                    value={formData.subordinateCourtInfo["District"]}
                                    onChange={(e) => handleNestedChange(e, 'subordinateCourtInfo')}
                                />
                            </div>
                        </div>
                    </div>

                    <h2 className='hc-title'>Petitioner And Advocate</h2>
                    <div className='div-rem'>
                        <div className="other-court-form-group">
                            <div className='form-det'>
                                <label>Petitioner</label>
                                <input
                                    name="petitioner"
                                    value={formData.petitionerAndAdvocate.petitioner}
                                    onChange={(e) => handleNestedChange(e, 'petitionerAndAdvocate')}
                                />
                            </div>
                            <div className='form-det'>
                                <label>Advocate</label>
                                <input
                                    name="advocate"
                                    value={formData.petitionerAndAdvocate.advocate}
                                    onChange={(e) => handleNestedChange(e, 'petitionerAndAdvocate')}
                                />
                            </div>
                        </div>
                        <div className="other-court-form-group">
                            <div className='form-det'>
                                <label>Respondent</label>
                                <input
                                    name="respondent"
                                    value={formData.petitionerAndAdvocate.respondent}
                                    onChange={(e) => handleNestedChange(e, 'petitionerAndAdvocate')}
                                />
                            </div>
                            <div className='form-det'>
                                <label>Respondent Advocate</label>
                                <input
                                    name="respondentAdvocate"
                                    value={formData.petitionerAndAdvocate.respondentAdvocate}
                                    onChange={(e) => handleNestedChange(e, 'petitionerAndAdvocate')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* <h2 className='hc-title'>IA Details</h2>
                    {formData.iaDetails.map((item, index) => (
                        <div className='div-rem' key={index}>
                            <div>
                                <div className="other-court-form-group">
                                    <div className='form-det'>
                                        <label>IA Number</label>
                                        <input
                                            type="text"
                                            ref={el => iaDetailsRefs.current[index] = el}
                                            value={item.iaNumber}
                                            onChange={(e) => handleArrayChange(index, 'iaDetails', 'iaNumber', e.target.value)}
                                        />
                                    </div>
                                    <div className='form-det'>
                                        <label>Party</label>
                                        <input
                                            type="text"
                                            value={item.party}
                                            onChange={(e) => handleArrayChange(index, 'iaDetails', 'party', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="other-court-form-group">
                                    <div className='form-det'>
                                        <label>Filing Date</label>
                                        <input
                                            type="date"
                                            value={item.filingDate}
                                            onChange={(e) => handleArrayChange(index, 'iaDetails', 'filingDate', e.target.value)}
                                        />
                                    </div>
                                    <div className='form-det'>
                                        <label>Next Date</label>
                                        <input
                                            type="date"
                                            value={item.nextDate}
                                            onChange={(e) => handleArrayChange(index, 'iaDetails', 'nextDate', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="other-court-form-group">
                                    <div className='form-det'>
                                        <label>IA Status</label>
                                        <input
                                            type="text"
                                            value={item.iaStatus}
                                            onChange={(e) => handleArrayChange(index, 'iaDetails', 'iaStatus', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className='rem-div-btn'>
                                <button type="button" className='court-remove-case' onClick={() => removeArrayItem(index, 'iaDetails')}>Remove</button>
                            </div>
                        </div>
                    ))}
                    <div className='add-div-btn'>
                        <button type="button" className='court-add-case' onClick={() => addArrayItem('iaDetails')}>+ IA Detail</button>
                    </div> */}

                    <h2 className='hc-title'>IA Details</h2>
                    {formData.iaDetails.map((item, index) => {
                        const isExpanded = index === activeIaDetailsIndex;
                        const hasData = item.iaNumber || item.party || item.iaStatus;

                        return (
                            <div className={`div-rem ${!isExpanded && hasData ? 'collapsed' : ''}`} key={index}>
                                {!isExpanded && hasData ? (
                                    <div className="summary-view">
                                        <div className="summary-details">
                                            <div className="summary-item">
                                                <span className="summary-label">IA No:</span>
                                                <span className="summary-value">{item.iaNumber || 'N/A'}</span>
                                            </div>
                                            <div className="summary-item">
                                                <span className="summary-label">Status:</span>
                                                <span className="summary-value">{item.iaStatus || 'N/A'}</span>
                                            </div>
                                        </div>
                                        <div className="summary-actions">
                                            <button type="button" className="edit-btn" onClick={() => setActiveIaDetailsIndex(index)}>Edit</button>
                                            <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'iaDetails')}>Remove</button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div>
                                            <div className="other-court-form-group">
                                                <div className='form-det'>
                                                    <label>IA Number</label>
                                                    <input
                                                        type="text"
                                                        ref={el => iaDetailsRefs.current[index] = el}
                                                        value={item.iaNumber}
                                                        onChange={(e) => handleArrayChange(index, 'iaDetails', 'iaNumber', e.target.value)}
                                                    />
                                                </div>
                                                <div className='form-det'>
                                                    <label>Party</label>
                                                    <input
                                                        type="text"
                                                        value={item.party}
                                                        onChange={(e) => handleArrayChange(index, 'iaDetails', 'party', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="other-court-form-group">
                                                <div className='form-det'>
                                                    <label>Filing Date</label>
                                                    <input
                                                        type="date"
                                                        value={item.filingDate}
                                                        onChange={(e) => handleArrayChange(index, 'iaDetails', 'filingDate', e.target.value)}
                                                    />
                                                </div>
                                                <div className='form-det'>
                                                    <label>Next Date</label>
                                                    <input
                                                        type="date"
                                                        value={item.nextDate}
                                                        onChange={(e) => handleArrayChange(index, 'iaDetails', 'nextDate', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="other-court-form-group">
                                                <div className='form-det'>
                                                    <label>IA Status</label>
                                                    <input
                                                        type="text"
                                                        value={item.iaStatus}
                                                        onChange={(e) => handleArrayChange(index, 'iaDetails', 'iaStatus', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className='rem-div-btn'>
                                            <button type="button" className='court-remove-case' onClick={() => removeArrayItem(index, 'iaDetails')}>Remove</button>
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                    <div className='add-div-btn'>
                        <button type="button" className='court-add-case' onClick={() => addArrayItem('iaDetails')}>+ IA Detail</button>
                    </div>

                    {/* <h2 className='hc-title'>Order Info</h2>
                    {formData.orderInfo.map((item, index) => {
                        const orderKey = `order_${index}`;
                        const isUploading = uploadingOrders[orderKey];
                        const uploadProgress = uploadProgressOrders[orderKey] || 0;

                        return (
                            <div className='div-rem' key={index}>
                                <div>
                                    <div className="other-court-form-group">
                                        <div className='form-det'>
                                            <label>Order Number</label>
                                            <input
                                                type="text"
                                                ref={el => orderInfoRefs.current[index] = el}
                                                value={item.orderNumber}
                                                onChange={(e) => handleArrayChange(index, 'orderInfo', 'orderNumber', e.target.value)}
                                            />
                                        </div>
                                        <div className='form-det'>
                                            <label>Order On</label>
                                            <input
                                                type="text"
                                                value={item.orderOn}
                                                onChange={(e) => handleArrayChange(index, 'orderInfo', 'orderOn', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="other-court-form-group">
                                        <div className='form-det'>
                                            <label>Judge</label>
                                            <input
                                                type="text"
                                                value={item.judge}
                                                onChange={(e) => handleArrayChange(index, 'orderInfo', 'judge', e.target.value)}
                                            />
                                        </div>
                                        <div className='form-det'>
                                            <label>Order Date</label>
                                            <input
                                                type="date"
                                                value={item.orderDate}
                                                onChange={(e) => handleArrayChange(index, 'orderInfo', 'orderDate', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="other-court-form-group">
                                        <div className='form-det'>
                                            <label>Order PDF</label>
                                            <input
                                                type="file"
                                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.mp4,.avi,.mov"
                                                onChange={(e) => handleOrderFileChange(e, index)}
                                                style={{ display: 'none' }}
                                                id={`orderFile_${index}`}
                                                disabled={isUploading}
                                            />

                                            {!item.orderPdfLink && !isUploading && (
                                                <div
                                                    className="upload-placeholder"
                                                    onClick={() => document.getElementById(`orderFile_${index}`).click()}
                                                    style={{
                                                        border: '2px dashed #e2e8f0',
                                                        borderRadius: '0.375rem',
                                                        padding: '1rem',
                                                        textAlign: 'center',
                                                        cursor: 'pointer',
                                                        background: '#fafafa'
                                                    }}
                                                >
                                                    <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                                        Click to upload order file
                                                    </span>
                                                </div>
                                            )}

                                            {isUploading && (
                                                <div className="upload-progress">
                                                    <div className="progress-bar" style={{
                                                        width: '100%',
                                                        height: '8px',
                                                        backgroundColor: '#e5e7eb',
                                                        borderRadius: '4px',
                                                        overflow: 'hidden',
                                                        marginBottom: '0.5rem'
                                                    }}>
                                                        <div
                                                            className="progress-fill"
                                                            style={{
                                                                width: `${uploadProgress}%`,
                                                                height: '100%',
                                                                backgroundColor: '#3b82f6',
                                                                transition: 'width 0.3s ease'
                                                            }}
                                                        />
                                                    </div>
                                                    <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                                                        {uploadProgress}% uploaded
                                                    </p>
                                                </div>
                                            )}

                                            {item.orderPdfLink && !isUploading && (
                                                <div className="uploaded-file" style={{
                                                    marginTop: '1rem',
                                                    padding: '0.75rem 1rem',
                                                    backgroundColor: '#f0f9ff',
                                                    border: '1px solid #bae6fd',
                                                    borderRadius: '0.375rem',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    gap: '1rem'
                                                }}>
                                                    <a
                                                        href={`${process.env.REACT_APP_BACKEND_MAIN_URL_PATH}ftp/view/${item.orderPdfLink}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            color: '#0369a1',
                                                            textDecoration: 'none',
                                                            fontWeight: '500',
                                                            fontSize: '0.875rem',
                                                            flex: 1
                                                        }}
                                                    >
                                                        View Uploaded Order File
                                                    </a>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveOrderFile(index)}
                                                        style={{
                                                            backgroundColor: '#ef4444',
                                                            color: 'white',
                                                            border: 'none',
                                                            padding: '0.375rem 0.75rem',
                                                            borderRadius: '0.25rem',
                                                            fontSize: '0.75rem',
                                                            fontWeight: '500',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className='rem-div-btn'>
                                    <button type="button" className='court-remove-case' onClick={() => removeArrayItem(index, 'orderInfo')}>Remove</button>
                                </div>
                            </div>
                        );
                    })}
                    <div className='add-div-btn'>
                        <button type="button" className='court-add-case' onClick={() => addArrayItem('orderInfo')}>+ Order Info</button>
                    </div> */}

                    <h2 className='hc-title'>Order Info</h2>
                    {formData.orderInfo.map((item, index) => {
                        const isExpanded = index === activeOrderInfoIndex;
                        const hasData = item.orderNumber || item.orderDate || item.judge;
                        const orderKey = `order_${index}`;
                        const isUploading = uploadingOrders[orderKey];
                        const uploadProgress = uploadProgressOrders[orderKey] || 0;

                        return (
                            <div className={`div-rem ${!isExpanded && hasData ? 'collapsed' : ''}`} key={index}>
                                {!isExpanded && hasData ? (
                                    <div className="summary-view">
                                        <div className="summary-details">
                                            <div className="summary-item">
                                                <span className="summary-label">Order No:</span>
                                                <span className="summary-value">{item.orderNumber || 'N/A'}</span>
                                            </div>
                                            <div className="summary-item">
                                                <span className="summary-label">Date:</span>
                                                <span className="summary-value">{item.orderDate || 'N/A'}</span>
                                            </div>
                                        </div>
                                        <div className="summary-actions">
                                            <button type="button" className="edit-btn" onClick={() => setActiveOrderInfoIndex(index)}>Edit</button>
                                            <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'orderInfo')}>Remove</button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div>
                                            <div className="other-court-form-group">
                                                <div className='form-det'>
                                                    <label>Order Number</label>
                                                    <input
                                                        type="text"
                                                        ref={el => orderInfoRefs.current[index] = el}
                                                        value={item.orderNumber}
                                                        onChange={(e) => handleArrayChange(index, 'orderInfo', 'orderNumber', e.target.value)}
                                                    />
                                                </div>
                                                <div className='form-det'>
                                                    <label>Order On</label>
                                                    <input
                                                        type="text"
                                                        value={item.orderOn}
                                                        onChange={(e) => handleArrayChange(index, 'orderInfo', 'orderOn', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="other-court-form-group">
                                                <div className='form-det'>
                                                    <label>Judge</label>
                                                    <input
                                                        type="text"
                                                        value={item.judge}
                                                        onChange={(e) => handleArrayChange(index, 'orderInfo', 'judge', e.target.value)}
                                                    />
                                                </div>
                                                <div className='form-det'>
                                                    <label>Order Date</label>
                                                    <input
                                                        type="date"
                                                        value={item.orderDate}
                                                        onChange={(e) => handleArrayChange(index, 'orderInfo', 'orderDate', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="other-court-form-group">
                                                <div className='form-det'>
                                                    <label>Order PDF</label>
                                                    <input
                                                        type="file"
                                                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.mp4,.avi,.mov"
                                                        onChange={(e) => handleOrderFileChange(e, index)}
                                                        style={{ display: 'none' }}
                                                        id={`orderFile_${index}`}
                                                        disabled={isUploading}
                                                    />

                                                    {!item.orderPdfLink && !isUploading && (
                                                        <div
                                                            className="upload-placeholder"
                                                            onClick={() => document.getElementById(`orderFile_${index}`).click()}
                                                            style={{
                                                                border: '2px dashed #e2e8f0',
                                                                borderRadius: '0.375rem',
                                                                padding: '1rem',
                                                                textAlign: 'center',
                                                                cursor: 'pointer',
                                                                background: '#fafafa'
                                                            }}
                                                        >
                                                            <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                                                Click to upload order file
                                                            </span>
                                                        </div>
                                                    )}

                                                    {isUploading && (
                                                        <div className="upload-progress">
                                                            <div className="progress-bar" style={{
                                                                width: '100%',
                                                                height: '8px',
                                                                backgroundColor: '#e5e7eb',
                                                                borderRadius: '4px',
                                                                overflow: 'hidden',
                                                                marginBottom: '0.5rem'
                                                            }}>
                                                                <div
                                                                    className="progress-fill"
                                                                    style={{
                                                                        width: `${uploadProgress}%`,
                                                                        height: '100%',
                                                                        backgroundColor: '#3b82f6',
                                                                        transition: 'width 0.3s ease'
                                                                    }}
                                                                />
                                                            </div>
                                                            <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                                                                {uploadProgress}% uploaded
                                                            </p>
                                                        </div>
                                                    )}

                                                    {item.orderPdfLink && !isUploading && (
                                                        <div className="uploaded-file" style={{
                                                            marginTop: '1rem',
                                                            padding: '0.75rem 1rem',
                                                            backgroundColor: '#f0f9ff',
                                                            border: '1px solid #bae6fd',
                                                            borderRadius: '0.375rem',
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            gap: '1rem'
                                                        }}>
                                                            <a
                                                                href={`${process.env.REACT_APP_BACKEND_MAIN_URL_PATH}ftp/view/${item.orderPdfLink}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{
                                                                    color: '#0369a1',
                                                                    textDecoration: 'none',
                                                                    fontWeight: '500',
                                                                    fontSize: '0.875rem',
                                                                    flex: 1
                                                                }}
                                                            >
                                                                View Uploaded Order File
                                                            </a>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveOrderFile(index)}
                                                                style={{
                                                                    backgroundColor: '#ef4444',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    padding: '0.375rem 0.75rem',
                                                                    borderRadius: '0.25rem',
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: '500',
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                Delete
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className='rem-div-btn'>
                                            <button type="button" className='court-remove-case' onClick={() => removeArrayItem(index, 'orderInfo')}>Remove</button>
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                    <div className='add-div-btn'>
                        <button type="button" className='court-add-case' onClick={() => addArrayItem('orderInfo')}>+ Order Info</button>
                    </div>



                    {/* <h2 className='hc-title'>Transfer Documents</h2>
                    {formData.transferDocs.map((item, index) => (
                        <div className='div-rem' key={index}>
                            <div>
                                <div className="other-court-form-group">
                                    <div className='form-det'>
                                        <label>Sr No</label>
                                        <input
                                            type="text"
                                            ref={el => transferDocsRefs.current[index] = el}
                                            value={item.srNo}
                                            onChange={(e) => handleArrayChange(index, 'transferDocs', 'srNo', e.target.value)}
                                        />
                                    </div>
                                    <div className='form-det'>
                                        <label>Document No</label>
                                        <input
                                            type="text"
                                            value={item.documentNo}
                                            onChange={(e) => handleArrayChange(index, 'transferDocs', 'documentNo', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="other-court-form-group">
                                    <div className='form-det'>
                                        <label>Date of Receiving</label>
                                        <input
                                            type="date"
                                            value={item.dateOfReceiving}
                                            onChange={(e) => handleArrayChange(index, 'transferDocs', 'dateOfReceiving', e.target.value)}
                                        />
                                    </div>
                                    <div className='form-det'>
                                        <label>Filed By</label>
                                        <input
                                            type="text"
                                            value={item.filedBy}
                                            onChange={(e) => handleArrayChange(index, 'transferDocs', 'filedBy', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="other-court-form-group">
                                    <div className='form-det'>
                                        <label>Advocate Name</label>
                                        <input
                                            type="text"
                                            value={item.advocateName}
                                            onChange={(e) => handleArrayChange(index, 'transferDocs', 'advocateName', e.target.value)}
                                        />
                                    </div>
                                    <div className='form-det'>
                                        <label>Document Filed</label>
                                        <input
                                            type="text"
                                            value={item.documentFiled}
                                            onChange={(e) => handleArrayChange(index, 'transferDocs', 'documentFiled', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className='rem-div-btn'>
                                <button type="button" className='court-remove-case' onClick={() => removeArrayItem(index, 'transferDocs')}>Remove</button>
                            </div>
                        </div>
                    ))}
                    <div className='add-div-btn'>
                        <button type="button" className='court-add-case' onClick={() => addArrayItem('transferDocs')}>+ Transfer Document</button>
                    </div> */}

                    <h2 className='hc-title'>Transfer Documents</h2>
                    {formData.transferDocs.map((item, index) => {
                        const isExpanded = index === activeTransferDocsIndex;
                        const hasData = item.documentNo || item.documentFiled || item.filedBy;

                        return (
                            <div className={`div-rem ${!isExpanded && hasData ? 'collapsed' : ''}`} key={index}>
                                {!isExpanded && hasData ? (
                                    <div className="summary-view">
                                        <div className="summary-details">
                                            <div className="summary-item">
                                                <span className="summary-label">Doc No:</span>
                                                <span className="summary-value">{item.documentNo || 'N/A'}</span>
                                            </div>
                                            <div className="summary-item">
                                                <span className="summary-label">Document:</span>
                                                <span className="summary-value">{item.documentFiled || 'N/A'}</span>
                                            </div>
                                        </div>
                                        <div className="summary-actions">
                                            <button type="button" className="edit-btn" onClick={() => setActiveTransferDocsIndex(index)}>Edit</button>
                                            <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'transferDocs')}>Remove</button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div>
                                            <div className="other-court-form-group">
                                                <div className='form-det'>
                                                    <label>Sr No</label>
                                                    <input
                                                        type="text"
                                                        ref={el => transferDocsRefs.current[index] = el}
                                                        value={item.srNo}
                                                        onChange={(e) => handleArrayChange(index, 'transferDocs', 'srNo', e.target.value)}
                                                    />
                                                </div>
                                                <div className='form-det'>
                                                    <label>Document No</label>
                                                    <input
                                                        type="text"
                                                        value={item.documentNo}
                                                        onChange={(e) => handleArrayChange(index, 'transferDocs', 'documentNo', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="other-court-form-group">
                                                <div className='form-det'>
                                                    <label>Date of Receiving</label>
                                                    <input
                                                        type="date"
                                                        value={item.dateOfReceiving}
                                                        onChange={(e) => handleArrayChange(index, 'transferDocs', 'dateOfReceiving', e.target.value)}
                                                    />
                                                </div>
                                                <div className='form-det'>
                                                    <label>Filed By</label>
                                                    <input
                                                        type="text"
                                                        value={item.filedBy}
                                                        onChange={(e) => handleArrayChange(index, 'transferDocs', 'filedBy', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="other-court-form-group">
                                                <div className='form-det'>
                                                    <label>Advocate Name</label>
                                                    <input
                                                        type="text"
                                                        value={item.advocateName}
                                                        onChange={(e) => handleArrayChange(index, 'transferDocs', 'advocateName', e.target.value)}
                                                    />
                                                </div>
                                                <div className='form-det'>
                                                    <label>Document Filed</label>
                                                    <input
                                                        type="text"
                                                        value={item.documentFiled}
                                                        onChange={(e) => handleArrayChange(index, 'transferDocs', 'documentFiled', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className='rem-div-btn'>
                                            <button type="button" className='court-remove-case' onClick={() => removeArrayItem(index, 'transferDocs')}>Remove</button>
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                    <div className='add-div-btn'>
                        <button type="button" className='court-add-case' onClick={() => addArrayItem('transferDocs')}>+ Transfer Document</button>
                    </div>

                    {/* <h2 className='hc-title'>Objection Details</h2>
                    {formData.objectionDetails.map((item, index) => (
                        <div className='div-rem' key={index}>
                            <div>
                                <div className="other-court-form-group">
                                    <div className='form-det'>
                                        <label>Sr No</label>
                                        <input
                                            ref={el => objectionDetailsRefs.current[index] = el}
                                            value={item.srNo}
                                            onChange={(e) => handleArrayChange(index, 'objectionDetails', 'srNo', e.target.value)}
                                        />
                                    </div>
                                    <div className='form-det'>
                                        <label>Scrutiny Date</label>
                                        <input
                                            type="date"
                                            value={item.scrutinyDate}
                                            onChange={(e) => handleArrayChange(index, 'objectionDetails', 'scrutinyDate', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="other-court-form-group">
                                    <div className='form-det'>
                                        <label>Objection</label>
                                        <input
                                            value={item.objection}
                                            onChange={(e) => handleArrayChange(index, 'objectionDetails', 'objection', e.target.value)}
                                        />
                                    </div>
                                    <div className='form-det'>
                                        <label>Compliance Date</label>
                                        <input
                                            type="date"
                                            value={item.complianceDate}
                                            onChange={(e) => handleArrayChange(index, 'objectionDetails', 'complianceDate', e.target.value)}
                                        />
                                    </div>
                                    <div className='form-det'>
                                        <label>Receipt Date</label>
                                        <input
                                            type="date"
                                            value={item.receiptDate}
                                            onChange={(e) => handleArrayChange(index, 'objectionDetails', 'receiptDate', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className='rem-div-btn'>
                                <button type="button" className='court-remove-case' onClick={() => removeArrayItem(index, 'objectionDetails')}>Remove</button>
                            </div>
                        </div>
                    ))}
                    <div className='add-div-btn'>
                        <button type="button" className='court-add-case' onClick={() => addArrayItem('objectionDetails')}>+ Objection Details</button>
                    </div> */}

                    <h2 className='hc-title'>Objection Details</h2>
                    {formData.objectionDetails.map((item, index) => {
                        const isExpanded = index === activeObjectionDetailsIndex;
                        const hasData = item.objection || item.scrutinyDate;

                        return (
                            <div className={`div-rem ${!isExpanded && hasData ? 'collapsed' : ''}`} key={index}>
                                {!isExpanded && hasData ? (
                                    <div className="summary-view">
                                        <div className="summary-details">
                                            <div className="summary-item">
                                                <span className="summary-label">Objection:</span>
                                                <span className="summary-value">{item.objection || 'N/A'}</span>
                                            </div>
                                            <div className="summary-item">
                                                <span className="summary-label">Date:</span>
                                                <span className="summary-value">{item.scrutinyDate || 'N/A'}</span>
                                            </div>
                                        </div>
                                        <div className="summary-actions">
                                            <button type="button" className="edit-btn" onClick={() => setActiveObjectionDetailsIndex(index)}>Edit</button>
                                            <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'objectionDetails')}>Remove</button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div>
                                            <div className="other-court-form-group">
                                                <div className='form-det'>
                                                    <label>Sr No</label>
                                                    <input
                                                        ref={el => objectionDetailsRefs.current[index] = el}
                                                        value={item.srNo}
                                                        onChange={(e) => handleArrayChange(index, 'objectionDetails', 'srNo', e.target.value)}
                                                    />
                                                </div>
                                                <div className='form-det'>
                                                    <label>Scrutiny Date</label>
                                                    <input
                                                        type="date"
                                                        value={item.scrutinyDate}
                                                        onChange={(e) => handleArrayChange(index, 'objectionDetails', 'scrutinyDate', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="other-court-form-group">
                                                <div className='form-det'>
                                                    <label>Objection</label>
                                                    <input
                                                        value={item.objection}
                                                        onChange={(e) => handleArrayChange(index, 'objectionDetails', 'objection', e.target.value)}
                                                    />
                                                </div>
                                                <div className='form-det'>
                                                    <label>Compliance Date</label>
                                                    <input
                                                        type="date"
                                                        value={item.complianceDate}
                                                        onChange={(e) => handleArrayChange(index, 'objectionDetails', 'complianceDate', e.target.value)}
                                                    />
                                                </div>
                                                <div className='form-det'>
                                                    <label>Receipt Date</label>
                                                    <input
                                                        type="date"
                                                        value={item.receiptDate}
                                                        onChange={(e) => handleArrayChange(index, 'objectionDetails', 'receiptDate', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className='rem-div-btn'>
                                            <button type="button" className='court-remove-case' onClick={() => removeArrayItem(index, 'objectionDetails')}>Remove</button>
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                    <div className='add-div-btn'>
                        <button type="button" className='court-add-case' onClick={() => addArrayItem('objectionDetails')}>+ Objection Details</button>
                    </div>

                    {/* <h2 className='hc-title'>Documents</h2>
                    {formData.documents.map((item, index) => (
                        <div className='div-rem' key={index}>
                            <div>
                                <div className="other-court-form-group">
                                    <div className='form-det'>
                                        <label>Sr No</label>
                                        <input
                                            type="text"
                                            ref={el => documentsRefs.current[index] = el}
                                            value={item.srNo}
                                            onChange={(e) => handleArrayChange(index, 'documents', 'srNo', e.target.value)}
                                        />
                                    </div>
                                    <div className='form-det'>
                                        <label>Document No</label>
                                        <input
                                            type="text"
                                            value={item.documentNo}
                                            onChange={(e) => handleArrayChange(index, 'documents', 'documentNo', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="other-court-form-group">
                                    <div className='form-det'>
                                        <label>Date of Receiving</label>
                                        <input
                                            type="date"
                                            value={item.dateOfReceiving}
                                            onChange={(e) => handleArrayChange(index, 'documents', 'dateOfReceiving', e.target.value)}
                                        />
                                    </div>
                                    <div className='form-det'>
                                        <label>Filed By</label>
                                        <input
                                            type="text"
                                            value={item.filedBy}
                                            onChange={(e) => handleArrayChange(index, 'documents', 'filedBy', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="other-court-form-group">
                                    <div className='form-det'>
                                        <label>Advocate Name</label>
                                        <input
                                            type="text"
                                            value={item.advocateName}
                                            onChange={(e) => handleArrayChange(index, 'documents', 'advocateName', e.target.value)}
                                        />
                                    </div>
                                    <div className='form-det'>
                                        <label>Document Filed</label>
                                        <input
                                            type="text"
                                            value={item.documentFiled}
                                            onChange={(e) => handleArrayChange(index, 'documents', 'documentFiled', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className='rem-div-btn'>
                                <button type="button" className='court-remove-case' onClick={() => removeArrayItem(index, 'documents')}>Remove</button>
                            </div>
                        </div>
                    ))}
                    <div className='add-div-btn'>
                        <button type="button" className='court-add-case' onClick={() => addArrayItem('documents')}>+ Document</button>
                    </div> */}

                    <h2 className='hc-title'>Documents</h2>
                    {formData.documents.map((item, index) => {
                        const isExpanded = index === activeDocumentsIndex;
                        const hasData = item.documentNo || item.documentFiled || item.filedBy;

                        return (
                            <div className={`div-rem ${!isExpanded && hasData ? 'collapsed' : ''}`} key={index}>
                                {!isExpanded && hasData ? (
                                    <div className="summary-view">
                                        <div className="summary-details">
                                            <div className="summary-item">
                                                <span className="summary-label">Doc No:</span>
                                                <span className="summary-value">{item.documentNo || 'N/A'}</span>
                                            </div>
                                            <div className="summary-item">
                                                <span className="summary-label">Document:</span>
                                                <span className="summary-value">{item.documentFiled || 'N/A'}</span>
                                            </div>
                                        </div>
                                        <div className="summary-actions">
                                            <button type="button" className="edit-btn" onClick={() => setActiveDocumentsIndex(index)}>Edit</button>
                                            <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'documents')}>Remove</button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div>
                                            <div className="other-court-form-group">
                                                <div className='form-det'>
                                                    <label>Sr No</label>
                                                    <input
                                                        type="text"
                                                        ref={el => documentsRefs.current[index] = el}
                                                        value={item.srNo}
                                                        onChange={(e) => handleArrayChange(index, 'documents', 'srNo', e.target.value)}
                                                    />
                                                </div>
                                                <div className='form-det'>
                                                    <label>Document No</label>
                                                    <input
                                                        type="text"
                                                        value={item.documentNo}
                                                        onChange={(e) => handleArrayChange(index, 'documents', 'documentNo', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="other-court-form-group">
                                                <div className='form-det'>
                                                    <label>Date of Receiving</label>
                                                    <input
                                                        type="date"
                                                        value={item.dateOfReceiving}
                                                        onChange={(e) => handleArrayChange(index, 'documents', 'dateOfReceiving', e.target.value)}
                                                    />
                                                </div>
                                                <div className='form-det'>
                                                    <label>Filed By</label>
                                                    <input
                                                        type="text"
                                                        value={item.filedBy}
                                                        onChange={(e) => handleArrayChange(index, 'documents', 'filedBy', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="other-court-form-group">
                                                <div className='form-det'>
                                                    <label>Advocate Name</label>
                                                    <input
                                                        type="text"
                                                        value={item.advocateName}
                                                        onChange={(e) => handleArrayChange(index, 'documents', 'advocateName', e.target.value)}
                                                    />
                                                </div>
                                                <div className='form-det'>
                                                    <label>Document Filed</label>
                                                    <input
                                                        type="text"
                                                        value={item.documentFiled}
                                                        onChange={(e) => handleArrayChange(index, 'documents', 'documentFiled', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className='rem-div-btn'>
                                            <button type="button" className='court-remove-case' onClick={() => removeArrayItem(index, 'documents')}>Remove</button>
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                    <div className='add-div-btn'>
                        <button type="button" className='court-add-case' onClick={() => addArrayItem('documents')}>+ Document</button>
                    </div>

                    {/* <h2 className='hc-title'>History Case Data</h2>
                    {formData.historyCaseData.map((item, index) => (
                        <div className='div-rem' key={index}>
                            <div>
                                <div className="other-court-form-group">
                                    <div className='form-det'>
                                        <label>Cause List Type</label>
                                        <input
                                            type="text"
                                            ref={el => historyCaseDataRefs.current[index] = el}
                                            value={item.causeListType}
                                            onChange={(e) => handleArrayChange(index, 'historyCaseData', 'causeListType', e.target.value)}
                                        />
                                    </div>
                                    <div className='form-det'>
                                        <label>Judge</label>
                                        <input
                                            type="text"
                                            value={item.judge}
                                            onChange={(e) => handleArrayChange(index, 'historyCaseData', 'judge', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="other-court-form-group">
                                    <div className='form-det'>
                                        <label>Business On Date</label>
                                        <input
                                            type="date"
                                            value={item.businessOnDate}
                                            onChange={(e) => handleArrayChange(index, 'historyCaseData', 'businessOnDate', e.target.value)}
                                        />
                                    </div>
                                    <div className='form-det'>
                                        <label>Hearing Date</label>
                                        <input
                                            type="date"
                                            value={item.hearingDate}
                                            onChange={(e) => handleArrayChange(index, 'historyCaseData', 'hearingDate', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="other-court-form-group">
                                    <div className='form-det'>
                                        <label>Purpose of Hearing</label>
                                        <input
                                            type="text"
                                            value={item.purposeOfHearing}
                                            onChange={(e) => handleArrayChange(index, 'historyCaseData', 'purposeOfHearing', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className='rem-div-btn'>
                                <button type="button" className='court-remove-case' onClick={() => removeArrayItem(index, 'historyCaseData')}>Remove</button>
                            </div>
                        </div>
                    ))}
                    <div className='add-div-btn'>
                        <button type="button" className='court-add-case' onClick={() => addArrayItem('historyCaseData')}>+ History Case Data</button>
                    </div> */}

                    <h2 className='hc-title'>History Case Data</h2>
                    {formData.historyCaseData.map((item, index) => {
                        const isExpanded = index === activeHistoryCaseDataIndex;
                        const hasData = item.purposeOfHearing || item.hearingDate || item.judge;

                        return (
                            <div className={`div-rem ${!isExpanded && hasData ? 'collapsed' : ''}`} key={index}>
                                {!isExpanded && hasData ? (
                                    <div className="summary-view">
                                        <div className="summary-details">
                                            <div className="summary-item">
                                                <span className="summary-label">Purpose:</span>
                                                <span className="summary-value">{item.purposeOfHearing || 'N/A'}</span>
                                            </div>
                                            <div className="summary-item">
                                                <span className="summary-label">Date:</span>
                                                <span className="summary-value">{item.hearingDate || 'N/A'}</span>
                                            </div>
                                        </div>
                                        <div className="summary-actions">
                                            <button type="button" className="edit-btn" onClick={() => setActiveHistoryCaseDataIndex(index)}>Edit</button>
                                            <button type="button" className="court-remove-case" onClick={() => removeArrayItem(index, 'historyCaseData')}>Remove</button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div>
                                            <div className="other-court-form-group">
                                                <div className='form-det'>
                                                    <label>Cause List Type</label>
                                                    <input
                                                        type="text"
                                                        ref={el => historyCaseDataRefs.current[index] = el}
                                                        value={item.causeListType}
                                                        onChange={(e) => handleArrayChange(index, 'historyCaseData', 'causeListType', e.target.value)}
                                                    />
                                                </div>
                                                <div className='form-det'>
                                                    <label>Judge</label>
                                                    <input
                                                        type="text"
                                                        value={item.judge}
                                                        onChange={(e) => handleArrayChange(index, 'historyCaseData', 'judge', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="other-court-form-group">
                                                <div className='form-det'>
                                                    <label>Business On Date</label>
                                                    <input
                                                        type="date"
                                                        value={item.businessOnDate}
                                                        onChange={(e) => handleArrayChange(index, 'historyCaseData', 'businessOnDate', e.target.value)}
                                                    />
                                                </div>
                                                <div className='form-det'>
                                                    <label>Hearing Date</label>
                                                    <input
                                                        type="date"
                                                        value={item.hearingDate}
                                                        onChange={(e) => handleArrayChange(index, 'historyCaseData', 'hearingDate', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="other-court-form-group">
                                                <div className='form-det'>
                                                    <label>Purpose of Hearing</label>
                                                    <input
                                                        type="text"
                                                        value={item.purposeOfHearing}
                                                        onChange={(e) => handleArrayChange(index, 'historyCaseData', 'purposeOfHearing', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className='rem-div-btn'>
                                            <button type="button" className='court-remove-case' onClick={() => removeArrayItem(index, 'historyCaseData')}>Remove</button>
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                    <div className='add-div-btn'>
                        <button type="button" className='court-add-case' onClick={() => addArrayItem('historyCaseData')}>+ History Case Data</button>
                    </div>


                    <div className="oc-submit-section-form" ref={submitSectionRef}>
                        <button className="submit-btn" type="submit" disabled={isLoading}>
                            <span>{isLoading ? 'Processing...' : (isEdit ? 'Update Other Court Case' : 'Submit Other Court Case')}</span>
                        </button>
                    </div>
                </form>
                <div className="oc-submit-section">
                    <button className="submit-btn" onClick={handleTopSubmit} type="button" disabled={isLoading}>
                        <span>{isLoading ? 'Processing...' : (isEdit ? 'Update Other Court Case' : 'Submit Other Court Case')}</span>
                    </button>
                </div>
            </div>
        </>

    );
};